import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { API, fileUrl, formatAxiosError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Globe, Sparkles, Upload, Check, Loader2, Palette, Wand2, Mail, BellRing, CreditCard, Server, Send, Cpu, Users, Shield, Percent, BookOpen, ExternalLink, Webhook, RotateCw, ChevronDown, ChevronUp } from "lucide-react";
import EmailEditor from "../components/EmailEditor";
import TermsSettings from "../components/TermsSettings";
import GoCardlessSettings from "../components/GoCardlessSettings";

const WEBHOOK_EVENTS = [
  { value: "first_view", label: "Première consultation" },
  { value: "accepted", label: "Proposition acceptée" },
  { value: "refused", label: "Proposition refusée" },
  { value: "question", label: "Question du prospect" },
  { value: "deposit_paid", label: "Acompte réglé" },
  // Dossier client (portail partenaires)
  { value: "document_uploaded", label: "Pièce déposée" },
  { value: "sepa_mandate_signed", label: "Mandat SEPA signé" },
  { value: "sepa_mandate_failed", label: "Mandat SEPA en échec" },
  { value: "dossier_completed", label: "Dossier complet" },
  { value: "channels_selected", label: "Chaînes TV choisies" },
];

const WEBHOOK_STATUS_BADGE = {
  pending: { label: "En attente", variant: "secondary" },
  success: { label: "Livré", variant: "default" },
  failed: { label: "Échec", variant: "destructive" },
};

const STEPS = ["Lecture du site web", "Décodage du langage de marque", "Capture du logo & visuel", "Composition du thème"];

function BrandExtractorDialog({ open, setOpen, onDone }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const timer = useRef(null);

  const run = async () => {
    if (!url.trim()) return toast.error("Collez l'URL de votre site");
    setLoading(true);
    setStep(0);
    timer.current = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 1400);
    try {
      const { data } = await api.post("/brand/extract", { url, theme_name: name });
      clearInterval(timer.current);
      // L'API renvoie 200 { ok:false, error } en cas d'échec (site inaccessible,
      // LLM en erreur) → traiter comme une erreur au lieu d'un faux succès.
      if (data.ok === false) {
        toast.error(data.error || "Échec de l'extraction");
        return;
      }
      setStep(STEPS.length);
      toast.success(`Thème extrait : ${data.name}`);
      await onDone();
      setOpen(false);
      setUrl(""); setName("");
    } catch (e) {
      toast.error(formatAxiosError(e, "Échec de l'extraction"));
    } finally {
      clearInterval(timer.current);
      setLoading(false);
      setStep(0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !loading && setOpen(o)}>
      <DialogContent className="rounded-sm sm:max-w-lg">
        <DialogHeader>
          <div className="w-10 h-10 rounded-sm border border-border flex items-center justify-center mb-2">
            <Globe className="w-5 h-5 text-primary" />
          </div>
          <DialogTitle className="font-heading text-2xl">Extraire un thème depuis un site web</DialogTitle>
          <p className="text-sm text-muted-foreground">Collez une URL. Notre agent analyse la marque, télécharge le logo et compose un thème de proposition assorti.</p>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>URL du site web</Label>
            <div className="relative mt-1.5">
              <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="brand-url-input"
                placeholder="https://votre-entreprise.com" className="pl-9 rounded-sm h-11" disabled={loading} />
            </div>
          </div>
          <div>
            <Label>Nom du thème (optionnel)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="brand-name-input"
              placeholder="Nom de la société" className="mt-1.5 rounded-sm h-11" disabled={loading} />
          </div>

          {loading && (
            <div className="border border-border rounded-sm p-4 bg-primary/5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /> En cours…</span>
                <span className="text-xs text-muted-foreground">Agent de marque</span>
              </div>
              <div className="space-y-2">
                {STEPS.map((s, i) => (
                  <div key={i} className={`flex items-center gap-2 text-sm transition-opacity ${i <= step ? "opacity-100" : "opacity-40"}`}>
                    {i < step ? <Check className="w-4 h-4 text-emerald-500" /> : i === step ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <span className="w-4 h-4 rounded-full border border-border inline-block" />}
                    {s}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading} className="rounded-sm">Annuler</Button>
          <Button onClick={run} disabled={loading} data-testid="brand-extract-submit" className="rounded-sm bg-primary hover:bg-primary/90">
            {loading ? "Extraction…" : <><Sparkles className="w-4 h-4 mr-1" /> Extraire</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const EMAIL_TYPE_LABELS = {
  proposal_sent: "Envoi de la proposition (au prospect)",
  follow_up: "Relance 48 h (au prospect)",
  question: "Question reçue (notification interne)",
  decision: "Décision — accepté/refusé (notification interne)",
  invitation: "Invitation d'un membre",
  signature_confirmation: "Confirmation de signature (au signataire)",
  dossier_reminder: "Relance du dossier (au client)",
  dossier_completed: "Dossier complet (à l'équipe et au partenaire)",
};
const EMAIL_TYPE_ORDER = ["proposal_sent", "follow_up", "question", "decision", "signature_confirmation", "dossier_reminder", "dossier_completed", "invitation"];

function CompanyEmailTemplateCard({ activeCompany, tpl, onChanged }) {
  const hasOverride = !!tpl.override;
  const [enabled, setEnabled] = useState(hasOverride ? tpl.override.enabled !== false : false);
  const [showGlobal, setShowGlobal] = useState(false);

  const label = EMAIL_TYPE_LABELS[tpl.type] || tpl.type;
  const subject = (tpl.override?.subject) ?? tpl.global?.subject ?? "";
  const body = (tpl.override?.body) ?? tpl.global?.body ?? "";

  return (
    <div className="border border-border rounded-sm p-4 lg:p-6 space-y-4" data-testid={`company-email-${tpl.type}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium text-sm">{label}</p>
        {hasOverride && (
          <span className="text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`company-email-badge-${tpl.type}`}>
            Personnalisé
          </span>
        )}
      </div>

      {/* Modèle global — lecture seule repliée */}
      <div>
        <button type="button" onClick={() => setShowGlobal((s) => !s)}
          data-testid={`company-email-global-toggle-${tpl.type}`}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
          {showGlobal ? "Masquer le modèle global" : "Voir le modèle global (par défaut)"}
        </button>
        {showGlobal && (
          <div className="mt-2 rounded-sm border border-border bg-muted/40 p-3 space-y-1.5" data-testid={`company-email-global-${tpl.type}`}>
            <p className="text-xs"><span className="text-muted-foreground">Objet : </span><span className="font-medium">{tpl.global?.subject}</span></p>
            <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground leading-relaxed">{tpl.global?.body}</pre>
          </div>
        )}
      </div>

      {/* Toggle de personnalisation */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">Personnaliser pour cette société</p>
          <p className="text-xs text-muted-foreground">Remplace le modèle global par votre propre objet et corps.</p>
        </div>
        <Switch checked={enabled} data-testid={`company-email-enable-${tpl.type}`}
          onCheckedChange={setEnabled} />
      </div>

      {enabled && (
        <EmailEditor
          key={hasOverride ? "override" : "global"}
          type={tpl.type}
          label={label}
          pixel={tpl.pixel}
          variables={tpl.variables || []}
          subject={subject}
          body={body}
          previewUrl={`/companies/${activeCompany}/email-templates/preview`}
          showHeader={false}
          onSave={async ({ subject: s, body: b }) => {
            await api.put(`/companies/${activeCompany}/email-templates/${tpl.type}`, { subject: s, body: b, enabled: true });
            await onChanged();
          }}
          onReset={hasOverride ? async () => {
            await api.delete(`/companies/${activeCompany}/email-templates/${tpl.type}`);
            await onChanged();
          } : undefined}
        />
      )}
    </div>
  );
}

function CompanyEmailTemplates({ activeCompany, brand }) {
  const [templates, setTemplates] = useState(null);

  const load = async () => {
    if (!activeCompany) return;
    try {
      const { data } = await api.get(`/companies/${activeCompany}/email-templates`);
      const sorted = [...(data || [])].sort((a, b) => EMAIL_TYPE_ORDER.indexOf(a.type) - EMAIL_TYPE_ORDER.indexOf(b.type));
      setTemplates(sorted);
    } catch { setTemplates([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompany]);

  return (
    <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8" data-testid="company-email-templates">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-primary" />
        <h3 className="font-heading text-lg font-semibold">Modèles d'email · {brand.name}</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Personnalisez les emails envoyés pour cette société. Sans personnalisation, le modèle global de la plateforme est utilisé.
      </p>
      {templates === null ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun modèle disponible.</p>
      ) : (
        <div className="space-y-4">
          {templates.map((tpl) => (
            <CompanyEmailTemplateCard key={tpl.type} activeCompany={activeCompany} tpl={tpl} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { brand, activeCompany, role, isSuperadmin, fetchCompanies, user } = useAuth();
  const [form, setForm] = useState({ name: "", tagline: "", industry: "", accent_hex: "#0047FF", logo_file_id: null, subdomain: "", custom_domain: "", public_theme: "classic" });
  const [saving, setSaving] = useState(false);
  const [extractor, setExtractor] = useState(false);
  const fileRef = useRef(null);

  // Per-user, per-company sender email
  const [senderEmail, setSenderEmail] = useState("");
  const [savingSender, setSavingSender] = useState(false);

  // Company follow-up / deposit settings
  const [followForm, setFollowForm] = useState({ follow_up_enabled: true, follow_up_hours: 48, email_on_send: true, require_deposit: false });
  const [savingFollow, setSavingFollow] = useState(false);

  // Per-company SMTP server config
  const [smtpForm, setSmtpForm] = useState({ smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", smtp_from: "", smtp_tls: true });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  // Per-company sender domain (our servers, tenant's domain)
  const [sd, setSd] = useState(null); // { domain, status, from_local, records, dns_check }
  const [sdDomain, setSdDomain] = useState("");
  const [sdBusy, setSdBusy] = useState(false);

  // Per-company deposit (acompte) settings — provider-agnostic
  const [stripeForm, setStripeForm] = useState({ require_deposit: false, deposit_type: "percentage", deposit_value: 30, stripe_secret_key: "" });
  const [stripeKeySet, setStripeKeySet] = useState(false);
  const [savingDeposit, setSavingDeposit] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);

  // Per-company taux de TVA (fiscalité) — vide = non renseigné (mention "TVA en sus" seule)
  const [vatRate, setVatRate] = useState("");
  const [savingVat, setSavingVat] = useState(false);

  // Per-company PayPal config
  const [paypalForm, setPaypalForm] = useState({ paypal_client_id: "", paypal_client_secret: "", paypal_enabled: false });
  const [paypalSecretSet, setPaypalSecretSet] = useState(false);
  const [savingPaypal, setSavingPaypal] = useState(false);

  // Per-company LLM (AI provider) config
  const [llmForm, setLlmForm] = useState({ llm_provider: "emergent", llm_model: "", llm_api_key: "" });
  const [llmKeySet, setLlmKeySet] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [testingLlm, setTestingLlm] = useState(false);
  const [llmTest, setLlmTest] = useState(null);

  // Team members
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviting, setInviting] = useState(false);

  // MCP tokens
  const [mcpTokens, setMcpTokens] = useState([]);
  // Quota de clés API/MCP piloté par l'abonnement : {used, max, plan}. max: -1 illimité, 0 aucun accès.
  const [tokenQuota, setTokenQuota] = useState(null);
  const [newTokenName, setNewTokenName] = useState("");
  const [creatingToken, setCreatingToken] = useState(false);
  const [freshToken, setFreshToken] = useState(null);

  // Webhooks
  const [webhooks, setWebhooks] = useState([]);
  const [newWebhook, setNewWebhook] = useState({ url: "", name: "", events: [] });
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [freshWebhookSecret, setFreshWebhookSecret] = useState(null);
  const [testingWebhookId, setTestingWebhookId] = useState(null);
  const [expandedWebhookId, setExpandedWebhookId] = useState(null);
  const [webhookDeliveries, setWebhookDeliveries] = useState({});
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [replayingDeliveryId, setReplayingDeliveryId] = useState(null);

  // Password change
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const canManage = isSuperadmin || role === "owner" || role === "admin";

  const sync = () => setForm({
    name: brand.name || "", tagline: brand.tagline || "", industry: brand.industry || "",
    accent_hex: brand.accent_hex || "#0047FF", logo_file_id: brand.logo_file_id || null,
    subdomain: brand.subdomain || "", custom_domain: brand.custom_domain || "",
    public_theme: brand.public_theme || "classic",
  });
  useEffect(() => { sync(); /* eslint-disable-next-line */ }, [activeCompany, brand.name, brand.accent_hex, brand.logo_file_id, brand.public_theme]);

  useEffect(() => {
    setFollowForm({
      follow_up_enabled: brand.follow_up_enabled !== false,
      follow_up_hours: brand.follow_up_hours || 48,
      email_on_send: brand.email_on_send !== false,
      require_deposit: !!brand.require_deposit,
    });
    setSmtpForm({
      smtp_host: brand.smtp_host || "",
      smtp_port: brand.smtp_port || 587,
      smtp_user: brand.smtp_user || "",
      smtp_password: "",
      smtp_from: brand.smtp_from || "",
      smtp_tls: brand.smtp_tls !== false,
    });
    setSmtpConfigured(!!brand.smtp_host);
    setStripeForm({
      require_deposit: !!brand.require_deposit,
      deposit_type: brand.deposit_type || "percentage",
      deposit_value: brand.deposit_value != null ? brand.deposit_value : 30,
      stripe_secret_key: "",
    });
    setStripeKeySet(!!brand.stripe_key_set);
    setPaypalForm({ paypal_client_id: brand.paypal_client_id || "", paypal_client_secret: "", paypal_enabled: !!brand.paypal_enabled });
    setPaypalSecretSet(!!brand.paypal_secret_set);
    setLlmForm({ llm_provider: brand.llm_provider || "emergent", llm_model: brand.llm_model || "", llm_api_key: "" });
    setLlmKeySet(!!brand.llm_key_set);
    setVatRate(brand.vat_rate != null ? String(brand.vat_rate) : "");
  }, [activeCompany, brand.follow_up_enabled, brand.follow_up_hours, brand.require_deposit, brand.smtp_host, brand.smtp_user, brand.smtp_from, brand.smtp_port, brand.smtp_tls, brand.deposit_type, brand.deposit_value, brand.stripe_key_set, brand.paypal_client_id, brand.paypal_secret_set, brand.paypal_enabled, brand.llm_provider, brand.llm_model, brand.llm_key_set, brand.vat_rate]);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setSenderEmail((r.data.sender_emails || {})[activeCompany] || "");
    }).catch(() => {});
  }, [activeCompany]);

  useEffect(() => { if (canManage) fetchMembers(); /* eslint-disable-next-line */ }, [activeCompany]);
  useEffect(() => { fetchMcpTokens(); /* eslint-disable-next-line */ }, [activeCompany]);
  useEffect(() => { fetchWebhooks(); setExpandedWebhookId(null); /* eslint-disable-next-line */ }, [activeCompany]);

  const loadSd = () => api.get(`/companies/${activeCompany}/sender-domain`).then(({ data }) => setSd(data)).catch(() => setSd(null));
  useEffect(() => { if (activeCompany) loadSd(); /* eslint-disable-next-line */ }, [activeCompany]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${activeCompany}/settings`, form);
      await fetchCompanies();
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSaving(false); }
  };

  const saveSender = async () => {
    setSavingSender(true);
    try {
      await api.put("/settings/sender-email", { email: senderEmail });
      toast.success("Email d'envoi enregistré pour " + (brand.name || activeCompany));
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSavingSender(false); }
  };

  const saveFollow = async () => {
    setSavingFollow(true);
    try {
      await api.put(`/companies/${activeCompany}/settings`, {
        follow_up_enabled: followForm.follow_up_enabled,
        follow_up_hours: Number(followForm.follow_up_hours) || 48,
        email_on_send: followForm.email_on_send,
      });
      await fetchCompanies();
      toast.success("Paramètres de relance enregistrés");
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSavingFollow(false); }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const payload = {
        smtp_host: smtpForm.smtp_host,
        smtp_port: Number(smtpForm.smtp_port) || 587,
        smtp_user: smtpForm.smtp_user,
        smtp_from: smtpForm.smtp_from,
        smtp_tls: smtpForm.smtp_tls,
      };
      if (smtpForm.smtp_password) payload.smtp_password = smtpForm.smtp_password; // never overwrite with blank
      await api.put(`/companies/${activeCompany}/settings`, payload);
      await fetchCompanies();
      setSmtpForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Configuration SMTP enregistrée pour " + (brand.name || activeCompany));
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSavingSmtp(false); }
  };

  const requestSd = async () => {
    setSdBusy(true);
    try {
      await api.post(`/companies/${activeCompany}/sender-domain`, { domain: sdDomain.trim() });
      setSdDomain("");
      await loadSd();
      toast.success("Demande enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.message || "Erreur");
    } finally { setSdBusy(false); }
  };
  const verifySd = async () => {
    setSdBusy(true);
    try {
      const { data } = await api.post(`/companies/${activeCompany}/sender-domain/verify`);
      await loadSd();
      (data.spf && data.dkim && data.return_path) ? toast.success("DNS vérifié") : toast.message("Certains enregistrements manquent encore");
    } catch (e) {
      toast.error(formatAxiosError(e, "Échec de la vérification"));
    } finally { setSdBusy(false); }
  };
  const removeSd = async () => {
    setSdBusy(true);
    try {
      await api.delete(`/companies/${activeCompany}/sender-domain`);
      await loadSd();
    } catch {
      // noop
    } finally { setSdBusy(false); }
  };
  const activeFrom = sd?.status === "verified" ? `${sd.from_local}@${sd.domain}` : `${activeCompany}.proposition@acteio.com`;

  const saveDeposit = async () => {
    setSavingDeposit(true);
    try {
      await api.put(`/companies/${activeCompany}/settings`, {
        require_deposit: stripeForm.require_deposit,
        deposit_type: stripeForm.deposit_type,
        deposit_value: Number(stripeForm.deposit_value) || 0,
      });
      await fetchCompanies();
      toast.success("Configuration de l'acompte enregistrée");
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSavingDeposit(false); }
  };

  const saveVat = async () => {
    setSavingVat(true);
    try {
      await api.put(`/companies/${activeCompany}/settings`, {
        vat_rate: vatRate === "" ? null : Number(vatRate),
      });
      await fetchCompanies();
      toast.success("Taux de TVA enregistré");
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.detail || "Erreur");
    } finally { setSavingVat(false); }
  };

  const saveStripe = async () => {
    if (!stripeForm.stripe_secret_key) return;
    setSavingStripe(true);
    try {
      await api.put(`/companies/${activeCompany}/settings`, { stripe_secret_key: stripeForm.stripe_secret_key });
      await fetchCompanies();
      setStripeForm((f) => ({ ...f, stripe_secret_key: "" }));
      toast.success("Clé Stripe enregistrée");
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSavingStripe(false); }
  };

  const savePaypal = async () => {
    setSavingPaypal(true);
    try {
      const payload = {
        paypal_client_id: paypalForm.paypal_client_id,
        paypal_enabled: paypalForm.paypal_enabled,
      };
      if (paypalForm.paypal_client_secret) payload.paypal_client_secret = paypalForm.paypal_client_secret;
      await api.put(`/companies/${activeCompany}/settings`, payload);
      await fetchCompanies();
      setPaypalForm((f) => ({ ...f, paypal_client_secret: "" }));
      toast.success("Configuration PayPal enregistrée");
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSavingPaypal(false); }
  };

  const saveLlm = async () => {
    setSavingLlm(true);
    try {
      const payload = { llm_provider: llmForm.llm_provider, llm_model: llmForm.llm_model || "" };
      if (llmForm.llm_provider === "emergent") payload.llm_api_key = "";  // clear own key -> back to Emergent
      else if (llmForm.llm_api_key) payload.llm_api_key = llmForm.llm_api_key;
      await api.put(`/companies/${activeCompany}/settings`, payload);
      await fetchCompanies();
      setLlmForm((f) => ({ ...f, llm_api_key: "" }));
      toast.success("Configuration IA enregistrée pour " + (brand.name || activeCompany));
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally { setSavingLlm(false); }
  };

  const testLlm = async () => {
    setTestingLlm(true);
    setLlmTest(null);
    try {
      const payload = { llm_provider: llmForm.llm_provider, llm_model: llmForm.llm_model || "" };
      if (llmForm.llm_provider !== "emergent" && llmForm.llm_api_key) payload.llm_api_key = llmForm.llm_api_key;
      const { data } = await api.post(`/companies/${activeCompany}/llm-test`, payload);
      setLlmTest(data);
      if (data.ok) toast.success(`Connexion IA OK (${data.provider} · ${data.latency_ms} ms)`);
      else toast.error("Échec de la connexion IA");
    } catch (e) {
      setLlmTest({ ok: false, error: formatAxiosError(e) });
      toast.error("Échec du test IA");
    } finally { setTestingLlm(false); }
  };

  // Team members handlers
  const fetchMembers = async () => {
    if (!activeCompany) return;
    setLoadingMembers(true);
    try {
      const { data } = await api.get(`/companies/${activeCompany}/members`);
      setMembers(data);
    } catch { setMembers([]); }
    finally { setLoadingMembers(false); }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return toast.error("Email requis");
    setInviting(true);
    try {
      const { data } = await api.post(`/companies/${activeCompany}/members/invite`, { email: inviteEmail, name: inviteName, role: inviteRole });
      toast.success(data.isNew ? `Compte créé pour ${data.email}` : `${data.email} ajouté à l'équipe`);
      setInviteEmail(""); setInviteName("");
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
    finally { setInviting(false); }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.put(`/companies/${activeCompany}/members/${userId}/role`, { role });
      toast.success("Rôle mis à jour");
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Retirer ${name} de l'équipe ?`)) return;
    try {
      await api.delete(`/companies/${activeCompany}/members/${userId}`);
      toast.success(`${name} retiré`);
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
  };

  // MCP tokens handlers
  const fetchMcpTokens = async () => {
    try {
      const { data } = await api.get("/settings/mcp-tokens");
      setMcpTokens(data);
    } catch { setMcpTokens([]); }
    // Quota rechargé avec la liste : il varie à chaque création/révocation.
    try {
      const { data } = await api.get("/settings/mcp-tokens/quota");
      setTokenQuota(data);
    } catch { setTokenQuota(null); }
  };

  const createToken = async () => {
    if (!newTokenName.trim()) return toast.error("Donnez un nom au token");
    setCreatingToken(true);
    try {
      const { data } = await api.post("/settings/mcp-tokens", { name: newTokenName });
      setFreshToken(data.token);
      setNewTokenName("");
      fetchMcpTokens();
      toast.success("Token créé — copiez-le maintenant, il ne sera plus affiché !");
      setTimeout(() => setFreshToken(null), 60_000);
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
    finally { setCreatingToken(false); }
  };

  const revokeToken = async (id, name) => {
    if (!window.confirm(`Révoquer le token "${name}" ? Les outils MCP utilisant ce token cesseront de fonctionner.`)) return;
    try {
      await api.delete(`/settings/mcp-tokens/${id}`);
      toast.success("Token révoqué");
      setMcpTokens(t => t.filter(x => x.id !== id));
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
  };

  // Webhooks handlers
  const fetchWebhooks = async () => {
    try {
      const { data } = await api.get("/webhooks");
      setWebhooks(data);
    } catch { setWebhooks([]); }
  };

  const toggleNewWebhookEvent = (value) => {
    setNewWebhook((w) => ({
      ...w,
      events: w.events.includes(value) ? w.events.filter((e) => e !== value) : [...w.events, value],
    }));
  };

  const createWebhook = async () => {
    if (!newWebhook.url.trim()) return toast.error("L'URL du webhook est requise");
    if (newWebhook.events.length === 0) return toast.error("Sélectionnez au moins un événement");
    setCreatingWebhook(true);
    try {
      const { data } = await api.post("/webhooks", newWebhook);
      setFreshWebhookSecret(data.secret);
      setNewWebhook({ url: "", name: "", events: [] });
      fetchWebhooks();
      toast.success("Webhook créé — copiez le secret maintenant, il ne sera plus affiché !");
      setTimeout(() => setFreshWebhookSecret(null), 60_000);
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setCreatingWebhook(false); }
  };

  const toggleWebhookEnabled = async (wh) => {
    try {
      await api.patch(`/webhooks/${wh.id}`, { enabled: !wh.enabled });
      toast.success(wh.enabled ? "Webhook désactivé" : "Webhook réactivé");
      fetchWebhooks();
    } catch (e) { toast.error(formatAxiosError(e)); }
  };

  const deleteWebhook = async (id, url) => {
    if (!window.confirm(`Supprimer le webhook "${url}" ? Le journal de livraison associé sera perdu.`)) return;
    try {
      await api.delete(`/webhooks/${id}`);
      toast.success("Webhook supprimé");
      setWebhooks((w) => w.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatAxiosError(e)); }
  };

  const testWebhook = async (id) => {
    setTestingWebhookId(id);
    try {
      const { data } = await api.post(`/webhooks/${id}/test`);
      if (data.ok) toast.success(`Test réussi (HTTP ${data.response_status})`);
      else toast.error(`Échec du test${data.response_status ? ` (HTTP ${data.response_status})` : ""} : ${data.error || "erreur inconnue"}`);
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setTestingWebhookId(null); }
  };

  const loadDeliveries = async (id) => {
    if (expandedWebhookId === id) { setExpandedWebhookId(null); return; }
    setExpandedWebhookId(id);
    setLoadingDeliveries(true);
    try {
      const { data } = await api.get(`/webhooks/${id}/deliveries`, { params: { page: 1, pageSize: 10 } });
      setWebhookDeliveries((d) => ({ ...d, [id]: data.items }));
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setLoadingDeliveries(false); }
  };

  const replayDelivery = async (deliveryId, endpointId) => {
    setReplayingDeliveryId(deliveryId);
    try {
      await api.post(`/webhooks/deliveries/${deliveryId}/replay`);
      toast.success("Livraison rejouée — reprogrammée pour envoi immédiat");
      const { data } = await api.get(`/webhooks/${endpointId}/deliveries`, { params: { page: 1, pageSize: 10 } });
      setWebhookDeliveries((d) => ({ ...d, [endpointId]: data.items }));
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setReplayingDeliveryId(null); }
  };

  // Password change handler
  const changePassword = async () => {
    if (!pwForm.current || !pwForm.next) return toast.error("Remplissez tous les champs");
    if (pwForm.next !== pwForm.confirm) return toast.error("Les mots de passe ne correspondent pas");
    if (pwForm.next.length < 8) return toast.error("Minimum 8 caractères");
    setSavingPw(true);
    try {
      await api.put("/settings/password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Mot de passe mis à jour");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
    finally { setSavingPw(false); }
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const { data } = await api.post("/settings/smtp-test", {});
      if (data.simulated) toast.info("Mode simulation : aucun serveur SMTP configuré.");
      else if (data.sent) toast.success(`Email de test envoyé à ${data.to}`);
      else toast.warning(data.error ? `Email non envoyé : ${data.error}` : "Email non envoyé.");
    } catch (e) {
      toast.error(formatAxiosError(e, "Échec du test SMTP"));
    } finally { setTestingSmtp(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_file_id: data.id }));
      await api.put(`/companies/${activeCompany}/settings`, { ...form, logo_file_id: data.id });
      await fetchCompanies();
      toast.success("Logo mis à jour");
    } catch (err) { toast.error("Échec de l'upload"); }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Société · {brand.name}</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Paramètres</h1>
      </div>

      {/* Brand Extractor highlight */}
      <div className="bg-foreground text-white rounded-sm p-6 lg:p-8 mb-8 relative overflow-hidden">
        <div className="absolute right-6 top-6 opacity-10"><Wand2 className="w-24 h-24" /></div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white/60 mb-2">Brand Extractor</p>
        <h2 className="font-heading text-2xl font-bold mb-2 max-w-md">Votre thème, en un collage d'URL.</h2>
        <p className="text-white/70 max-w-lg mb-5">Collez l'URL de votre site : l'agent récupère vos couleurs, votre logo et votre style, puis les applique automatiquement à vos propositions.</p>
        <Button onClick={() => setExtractor(true)} data-testid="open-brand-extractor" className="rounded-sm bg-white text-foreground hover:bg-white/90 font-semibold">
          <Globe className="w-4 h-4 mr-2" /> Extraire depuis un site web
        </Button>
      </div>

      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-6">
        <h3 className="font-heading text-lg font-semibold">Identité de la société</h3>

        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-sm border border-border bg-white flex items-center justify-center overflow-hidden shrink-0 p-1.5">
            {form.logo_file_id
              ? <img src={fileUrl(form.logo_file_id)} alt="logo" className="w-full h-full object-contain" />
              : <span className="font-heading text-2xl font-bold" style={{ color: form.accent_hex }}>{(form.name[0] || "?").toUpperCase()}</span>}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm" data-testid="upload-logo-btn">
              <Upload className="w-4 h-4 mr-2" /> Importer un logo
            </Button>
            <p className="text-xs text-muted-foreground mt-2">PNG, JPG ou SVG · 6 Mo max</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Nom</Label><Input value={form.name} onChange={upd("name")} data-testid="settings-name" className="mt-1.5 rounded-sm" /></div>
          <div><Label>Secteur</Label><Input value={form.industry} onChange={upd("industry")} data-testid="settings-industry" className="mt-1.5 rounded-sm" /></div>
        </div>
        <div><Label>Accroche</Label><Input value={form.tagline} onChange={upd("tagline")} data-testid="settings-tagline" className="mt-1.5 rounded-sm" /></div>
        <div>
          <Label className="flex items-center gap-1.5"><Palette className="w-4 h-4" /> Couleur d'accent</Label>
          <div className="flex items-center gap-3 mt-1.5">
            <input type="color" value={form.accent_hex} onChange={upd("accent_hex")} data-testid="settings-accent" className="w-12 h-10 rounded-sm border border-border cursor-pointer" />
            <Input value={form.accent_hex} onChange={upd("accent_hex")} className="rounded-sm w-32 font-mono" />
            <span className="w-10 h-10 rounded-sm border border-border" style={{ backgroundColor: form.accent_hex }} />
          </div>
        </div>

        <div>
          <Label>Style de la page client</Label>
          <p className="text-xs text-muted-foreground mt-1">Présentation du lien envoyé au client pour lire et signer vos propositions.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-2" role="radiogroup" aria-label="Style de la page client">
            {[
              { value: "classic", title: "Classique", text: "Document sobre et épuré : sommaire, sections, récapitulatif." },
              { value: "premium", title: "Premium", text: "En-tête d'impact avec le prix et la signature, registre haut de gamme, mur de chaînes TV." },
            ].map((o) => (
              <button key={o.value} type="button" role="radio" aria-checked={form.public_theme === o.value}
                onClick={() => setForm((f) => ({ ...f, public_theme: o.value }))} data-testid={`settings-theme-${o.value}`}
                className={`text-left border rounded-sm p-4 transition-colors ${form.public_theme === o.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                <p className="font-medium text-sm">{o.title}{o.value === "classic" && <span className="ml-2 text-[11px] font-normal text-muted-foreground">par défaut</span>}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{o.text}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <Button onClick={save} disabled={saving} data-testid="save-settings" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {saving ? "Enregistrement…" : "Enregistrer les modifications"}
          </Button>
        </div>
      </div>

      {/* Domaine personnalisé */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-4 mt-8">
        <h3 className="font-heading text-lg font-semibold">Domaine</h3>
        <div>
          <Label>Sous-domaine</Label>
          <p className="text-xs text-muted-foreground mb-1.5">Ex. : <code>cleya</code> → cleya.votredomaine.com</p>
          <Input value={form.subdomain} onChange={upd("subdomain")} placeholder="cleya" data-testid="settings-subdomain" className="mt-0.5 rounded-sm" />
        </div>
        <div>
          <Label>Domaine personnalisé</Label>
          <p className="text-xs text-muted-foreground mb-1.5">Ex. : <code>proposals.cleya.fr</code> — créez un CNAME vers votre domaine principal</p>
          <Input value={form.custom_domain} onChange={upd("custom_domain")} placeholder="proposals.cleya.fr" data-testid="settings-custom-domain" className="mt-0.5 rounded-sm" />
        </div>
        <div className="pt-2">
          <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>

      {/* CGV — owner/admin, tous forfaits */}
      {canManage && <TermsSettings activeCompany={activeCompany} brand={brand} />}

      {/* Sender email — per user, per company */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-4 mt-8">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Votre email d'envoi · {brand.name}</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Cet email est utilisé comme expéditeur des notifications et relances pour <span className="font-medium text-foreground">{brand.name}</span>.
          Il est propre à vous et à cette société : si vous gérez plusieurs sociétés, définissez un email distinct pour chacune via le sélecteur de société.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} type="email" data-testid="sender-email-input"
            placeholder="vous@votre-societe.com" className="rounded-sm flex-1" />
          <Button onClick={saveSender} disabled={savingSender} data-testid="save-sender-email" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {savingSender ? "…" : "Enregistrer l'email"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Envoi des emails en <span className="font-medium">{smtpConfigured ? "envoi réel" : "mode simulation"}</span> pour {brand.name}. {smtpConfigured ? "Un serveur SMTP est configuré ci-dessous." : "Configurez un serveur SMTP ci-dessous pour activer l'envoi réel."}</p>
      </div>

      {/* SMTP server config — per company, owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Serveur SMTP · {brand.name}</h3>
            </div>
            <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold ${smtpConfigured ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-muted text-muted-foreground"}`} data-testid="smtp-status">
              {smtpConfigured ? "Envoi réel" : "Simulation"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Paramètres d'envoi propres à cette société. Chaque société peut avoir son propre serveur SMTP.</p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Serveur SMTP (hôte)</Label>
              <Input value={smtpForm.smtp_host} onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_host: e.target.value }))} data-testid="smtp-host" placeholder="smtp.votre-fournisseur.com" className="mt-1.5 rounded-sm" /></div>
            <div><Label>Port</Label>
              <Input type="number" value={smtpForm.smtp_port} onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_port: e.target.value }))} data-testid="smtp-port" placeholder="587" className="mt-1.5 rounded-sm" /></div>
            <div className="flex items-end justify-between gap-3 pb-1">
              <div><p className="text-sm font-medium">Chiffrement TLS</p><p className="text-xs text-muted-foreground">Recommandé (STARTTLS)</p></div>
              <Switch checked={smtpForm.smtp_tls} data-testid="smtp-tls" onCheckedChange={(v) => setSmtpForm((f) => ({ ...f, smtp_tls: v }))} />
            </div>
            <div><Label>Identifiant (utilisateur)</Label>
              <Input value={smtpForm.smtp_user} onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_user: e.target.value }))} data-testid="smtp-user" placeholder="hello@votre-societe.com" className="mt-1.5 rounded-sm" /></div>
            <div><Label>Mot de passe</Label>
              <Input type="password" value={smtpForm.smtp_password} onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_password: e.target.value }))} data-testid="smtp-password" placeholder={brand.smtp_password_set ? "•••••••• (inchangé)" : "Mot de passe SMTP"} className="mt-1.5 rounded-sm" /></div>
            <div className="sm:col-span-2"><Label>Adresse d'expéditeur (From)</Label>
              <Input value={smtpForm.smtp_from} onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_from: e.target.value }))} data-testid="smtp-from" placeholder='Votre Société <no-reply@votre-societe.com>' className="mt-1.5 rounded-sm" /></div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={saveSmtp} disabled={savingSmtp} data-testid="save-smtp" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {savingSmtp ? "Enregistrement…" : "Enregistrer le SMTP"}
            </Button>
            <Button onClick={testSmtp} disabled={testingSmtp} variant="outline" data-testid="test-smtp" className="rounded-sm">
              {testingSmtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Envoyer un email de test
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Le mot de passe est stocké de façon sécurisée et n'est jamais renvoyé. Laissez le champ vide pour le conserver.</p>
        </div>
      )}

      {/* Sender domain — our servers, tenant's own domain — owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 space-y-5 mt-8" data-testid="sender-domain-card">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Envoi des emails</h3>
          </div>
          <div className="text-sm rounded-sm px-3 py-2.5 border bg-muted/40 border-border text-muted-foreground">
            Aujourd'hui, vos emails partent de <code className="font-mono text-xs bg-muted px-1 rounded">{activeFrom}</code>.
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Recommandé — configurez votre propre SMTP</p>
            <p className="text-xs text-muted-foreground">Vos emails partiront de votre serveur, avec votre délivrabilité. (Section SMTP ci-dessus.)</p>
          </div>

          <div className="pt-3 border-t border-border space-y-3">
            <p className="text-sm font-medium">Ou : utilisez nos serveurs avec votre domaine</p>
            {!sd && (
              <div className="flex items-end gap-2 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <Label>Votre domaine d'envoi</Label>
                  <Input value={sdDomain} onChange={(e) => setSdDomain(e.target.value)} placeholder="votredomaine.fr" className="mt-1.5 rounded-sm" data-testid="sd-domain-input" />
                </div>
                <Button onClick={requestSd} disabled={sdBusy || !sdDomain.trim()} className="rounded-sm" data-testid="sd-request">Demander</Button>
              </div>
            )}
            {sd && sd.status === "pending" && !(sd.records || []).some((r) => r.label === "DKIM") && (
              <div className="text-sm rounded-sm px-3 py-2.5 border bg-amber-50 border-amber-200 text-amber-800">
                <strong>{sd.domain}</strong> — en préparation par notre équipe. Vos enregistrements DNS arriveront bientôt.
              </div>
            )}
            {sd && (sd.records || []).some((r) => r.label === "DKIM") && sd.status !== "verified" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Ajoutez ces enregistrements DNS chez votre registrar, puis cliquez « Vérifier ».</p>
                <div className="space-y-2">
                  {sd.records.map((r) => (
                    <div key={r.label} className="text-xs bg-muted rounded-sm p-2 font-mono break-all" data-testid={`sd-record-${r.label}`}>
                      <span className="inline-block w-24 text-muted-foreground">{r.label} ({r.type})</span>
                      <div><span className="text-muted-foreground">nom :</span> {r.name}</div>
                      <div><span className="text-muted-foreground">valeur :</span> {r.value} {sd.dns_check && <span>{(r.label === "SPF" ? sd.dns_check.spf : r.label === "DKIM" ? sd.dns_check.dkim : sd.dns_check.return_path) ? "✅" : "❌"}</span>}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={verifySd} disabled={sdBusy} variant="outline" className="rounded-sm" data-testid="sd-verify">Vérifier</Button>
                  <button onClick={removeSd} className="text-xs text-muted-foreground hover:text-destructive">Retirer</button>
                </div>
                {sd.status === "dns_ok" && <p className="text-sm text-emerald-700">DNS vérifié — en attente de validation finale par notre équipe.</p>}
              </div>
            )}
            {sd && sd.status === "verified" && (
              <div className="text-sm rounded-sm px-3 py-2.5 border bg-emerald-50 border-emerald-200 text-emerald-800 flex items-center justify-between">
                <span>✅ Actif — vos emails partent de <code className="font-mono text-xs bg-white px-1 rounded">{sd.from_local}@{sd.domain}</code></span>
                <button onClick={removeSd} className="text-xs text-emerald-700 hover:text-destructive">Changer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Follow-up & deposit — owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-6 mt-8">
          <div className="flex items-center gap-2">
            <BellRing className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Relances automatiques & acompte</h3>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-sm">Email automatique à l'envoi</p>
              <p className="text-xs text-muted-foreground">Envoie la proposition au prospect (email du client) dès le passage en « envoyée », avec le lien direct.</p>
            </div>
            <Switch checked={followForm.email_on_send} data-testid="email-on-send-toggle"
              onCheckedChange={(v) => setFollowForm((f) => ({ ...f, email_on_send: v }))} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-sm">Relance automatique</p>
              <p className="text-xs text-muted-foreground">Envoie un email de relance au prospect si la proposition n'a pas été décidée après le délai.</p>
            </div>
            <Switch checked={followForm.follow_up_enabled} data-testid="follow-up-toggle"
              onCheckedChange={(v) => setFollowForm((f) => ({ ...f, follow_up_enabled: v }))} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-sm">Délai avant relance</p>
              <p className="text-xs text-muted-foreground">En heures (par défaut 48 h après l'envoi).</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" min={1} value={followForm.follow_up_hours} data-testid="follow-up-hours"
                onChange={(e) => setFollowForm((f) => ({ ...f, follow_up_hours: e.target.value }))}
                className="rounded-sm w-24 text-center" disabled={!followForm.follow_up_enabled} />
              <span className="text-sm text-muted-foreground">h</span>
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={saveFollow} disabled={savingFollow} data-testid="save-follow-settings" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {savingFollow ? "Enregistrement…" : "Enregistrer les relances"}
            </Button>
          </div>
        </div>
      )}

      {/* Acompte — provider-agnostic master switch, owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Acompte · {brand.name}</h3>
          </div>
          <p className="text-sm text-muted-foreground">Demandez un acompte au prospect lors de l'acceptation. Encaissez-le via Stripe et/ou PayPal — configurez l'un, l'autre, ou les deux ci-dessous.</p>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-sm">Demander un acompte à la signature</p>
              <p className="text-xs text-muted-foreground">Le prospect signe puis règle l'acompte via le(s) moyen(s) de paiement configuré(s).</p>
            </div>
            <Switch checked={stripeForm.require_deposit} data-testid="deposit-toggle"
              onCheckedChange={(v) => setStripeForm((f) => ({ ...f, require_deposit: v }))} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Type d'acompte</Label>
              <div className="mt-1.5 flex rounded-sm border border-border overflow-hidden" data-testid="deposit-type">
                {[["percentage", "Pourcentage"], ["fixed", "Montant fixe"]].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setStripeForm((f) => ({ ...f, deposit_type: v }))}
                    data-testid={`deposit-type-${v}`}
                    className={`flex-1 h-10 text-sm font-medium transition-colors ${stripeForm.deposit_type === v ? "bg-primary text-white" : "bg-transparent text-muted-foreground hover:bg-muted"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{stripeForm.deposit_type === "fixed" ? "Montant de l'acompte" : "Pourcentage du total"}</Label>
              <div className="mt-1.5 relative">
                <Input type="number" min={0} value={stripeForm.deposit_value} data-testid="deposit-value"
                  onChange={(e) => setStripeForm((f) => ({ ...f, deposit_value: e.target.value }))} className="rounded-sm pr-9" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{stripeForm.deposit_type === "fixed" ? "€" : "%"}</span>
              </div>
            </div>
          </div>

          <div className="pt-1">
            <Button onClick={saveDeposit} disabled={savingDeposit} data-testid="save-deposit" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {savingDeposit ? "Enregistrement…" : "Enregistrer l'acompte"}
            </Button>
          </div>
        </div>
      )}

      {/* Fiscalité (TVA) — owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-4 mt-8">
          <div className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Fiscalité (TVA) · {brand.name}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Tous les tarifs saisis dans Acteio sont hors taxes (HT). Les documents (viewer, PDF, email)
            affichent toujours la mention HT. Renseignez ici votre taux de TVA pour qu'ils affichent
            aussi, automatiquement, le total TTC.
          </p>
          <div className="max-w-xs">
            <Label>Taux de TVA (%)</Label>
            <div className="mt-1.5 relative">
              <Input type="number" min={0} max={100} step="0.1" value={vatRate} data-testid="vat-rate"
                placeholder="Non renseigné" onChange={(e) => setVatRate(e.target.value)} className="rounded-sm pr-9" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              20 en métropole, 8,5 en Martinique/Guadeloupe/Réunion. Vide : les documents mentionnent
              « TVA en sus » sans calcul TTC.
            </p>
          </div>
          <div className="pt-1">
            <Button onClick={saveVat} disabled={savingVat} data-testid="save-vat" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {savingVat ? "Enregistrement…" : "Enregistrer la TVA"}
            </Button>
          </div>
        </div>
      )}

      {/* Stripe — payment rail, owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Paiement Stripe · {brand.name}</h3>
            </div>
            <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold ${stripeKeySet ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-muted text-muted-foreground"}`} data-testid="stripe-status">
              {stripeKeySet ? "Configuré" : "Non configuré"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Connectez votre compte Stripe pour encaisser l'acompte. Chaque société encaisse sur son propre compte Stripe.</p>

          <div>
            <Label>Clé secrète Stripe</Label>
            <Input type="password" value={stripeForm.stripe_secret_key} data-testid="stripe-key"
              onChange={(e) => setStripeForm((f) => ({ ...f, stripe_secret_key: e.target.value }))}
              placeholder={brand.stripe_key_set ? "•••••••• (inchangée)" : "sk_live_… ou sk_test_…"} className="mt-1.5 rounded-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Stockée de façon sécurisée, jamais renvoyée. Laissez vide pour conserver la clé actuelle.</p>
          </div>

          <div className="pt-1">
            <Button onClick={saveStripe} disabled={savingStripe || !stripeForm.stripe_secret_key} data-testid="save-stripe" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {savingStripe ? "Enregistrement…" : "Enregistrer Stripe"}
            </Button>
          </div>
        </div>
      )}

      {/* PayPal — owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Paiement PayPal · {brand.name}</h3>
            </div>
            <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold ${paypalSecretSet && paypalForm.paypal_enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-muted text-muted-foreground"}`} data-testid="paypal-status">
              {paypalForm.paypal_enabled ? (paypalSecretSet ? "Actif" : "Clé requise") : "Inactif"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Connectez PayPal pour encaisser l'acompte, en complément ou à la place de Stripe. Les deux moyens de paiement peuvent coexister.</p>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-sm">Activer PayPal</p>
              <p className="text-xs text-muted-foreground">Le prospect pourra choisir PayPal à la signature.</p>
            </div>
            <Switch checked={paypalForm.paypal_enabled} data-testid="paypal-toggle"
              onCheckedChange={(v) => setPaypalForm((f) => ({ ...f, paypal_enabled: v }))} />
          </div>

          <div>
            <Label>Client ID PayPal</Label>
            <Input type="text" value={paypalForm.paypal_client_id} data-testid="paypal-client-id"
              onChange={(e) => setPaypalForm((f) => ({ ...f, paypal_client_id: e.target.value }))}
              placeholder="AZ… (sandbox) ou votre Client ID live" className="mt-1.5 rounded-sm" />
          </div>

          <div>
            <Label>Client Secret PayPal</Label>
            <Input type="password" value={paypalForm.paypal_client_secret} data-testid="paypal-secret"
              onChange={(e) => setPaypalForm((f) => ({ ...f, paypal_client_secret: e.target.value }))}
              placeholder={paypalSecretSet ? "•••••••• (inchangé)" : "Votre Client Secret PayPal"} className="mt-1.5 rounded-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Stocké de façon sécurisée, jamais renvoyé. Laissez vide pour conserver le secret actuel.</p>
          </div>

          <div className="pt-1">
            <Button onClick={savePaypal} disabled={savingPaypal} data-testid="save-paypal" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {savingPaypal ? "Enregistrement…" : "Enregistrer PayPal"}
            </Button>
          </div>
        </div>
      )}

      {/* GoCardless (mandats SEPA) — owner/admin only */}
      {canManage && <GoCardlessSettings activeCompany={activeCompany} brand={brand} fetchCompanies={fetchCompanies} />}

      {/* LLM / AI provider — owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8" data-testid="llm-settings">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Cpu className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Moteur IA · {brand.name}</h3>
            </div>
            <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold ${llmForm.llm_provider !== "emergent" && llmKeySet ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-muted text-muted-foreground"}`} data-testid="llm-status">
              {llmForm.llm_provider === "emergent" ? "Défaut" : (llmKeySet ? "Clé propre" : "Clé requise")}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Choisissez le moteur de génération IA (propositions, modèles, extraction de marque).
            Sélectionnez un fournisseur et saisissez <span className="font-medium">votre propre clé API</span> pour que les appels aillent directement chez le fournisseur.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Fournisseur</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="llm-provider">
                {[["emergent", "Défaut"], ["openai", "OpenAI"], ["anthropic", "Anthropic"], ["gemini", "Gemini"]].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setLlmForm((f) => ({ ...f, llm_provider: v }))}
                    data-testid={`llm-provider-${v}`}
                    className={`h-10 text-sm font-medium rounded-sm border transition-colors ${llmForm.llm_provider === v ? "bg-primary text-white border-primary" : "bg-transparent text-muted-foreground border-border hover:bg-muted"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Modèle</Label>
              <Input value={llmForm.llm_model} onChange={(e) => setLlmForm((f) => ({ ...f, llm_model: e.target.value }))}
                data-testid="llm-model"
                placeholder={llmForm.llm_provider === "openai" ? "gpt-4o" : llmForm.llm_provider === "anthropic" ? "claude-sonnet-4-6" : llmForm.llm_provider === "gemini" ? "gemini-2-flash" : "gpt-4o (défaut)"}
                className="mt-1.5 rounded-sm" />
              <p className="text-xs text-muted-foreground mt-1.5">Laissez vide pour le modèle par défaut du fournisseur.</p>
            </div>
          </div>

          {llmForm.llm_provider !== "emergent" && (
            <div>
              <Label>Clé API {llmForm.llm_provider}</Label>
              <Input type="password" value={llmForm.llm_api_key} data-testid="llm-key"
                onChange={(e) => setLlmForm((f) => ({ ...f, llm_api_key: e.target.value }))}
                placeholder={llmKeySet ? "•••••••• (inchangée)" : "Votre clé API"} className="mt-1.5 rounded-sm" />
              <p className="text-xs text-muted-foreground mt-1.5">Stockée de façon sécurisée, jamais renvoyée. En cas d'échec, le fournisseur par défaut prend le relais.</p>
            </div>
          )}

          <div className="pt-1 flex items-center gap-3 flex-wrap">
            <Button onClick={saveLlm} disabled={savingLlm} data-testid="save-llm" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {savingLlm ? "Enregistrement…" : "Enregistrer le moteur IA"}
            </Button>
            <Button onClick={testLlm} disabled={testingLlm} variant="outline" data-testid="test-llm" className="rounded-sm font-semibold">
              {testingLlm ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Test en cours…</> : "Tester la connexion IA"}
            </Button>
          </div>
          {llmTest && (
            <div data-testid="llm-test-result" className={`text-sm rounded-sm px-3 py-2.5 border ${llmTest.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
              {llmTest.ok ? (
                <span><Check className="w-4 h-4 inline mr-1.5 -mt-0.5" />Connexion réussie — <strong>{llmTest.provider}</strong> · {llmTest.model} · {llmTest.latency_ms} ms. Réponse : « {llmTest.sample} »</span>
              ) : (
                <span><strong>Échec :</strong> {llmTest.error}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modèles d'email — owner/admin only */}
      {canManage && <CompanyEmailTemplates activeCompany={activeCompany} brand={brand} />}

      {/* Team members — owner/admin only */}
      {canManage && (
        <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Équipe · {brand.name}</h3>
          </div>

          {/* Liste membres */}
          <div className="space-y-2">
            {loadingMembers ? <p className="text-sm text-muted-foreground">Chargement…</p> : members.map(m => (
              <div key={m.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                  {(m.name || m.email)[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                <select value={m.role}
                  onChange={(e) => changeRole(m.id, e.target.value)}
                  disabled={m.id === user?.id}
                  className="text-xs border border-border rounded-sm px-2 py-1 bg-background">
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="user">Utilisateur</option>
                </select>
                {m.id !== user?.id && (
                  <button onClick={() => removeMember(m.id, m.name)}
                    className="text-xs text-muted-foreground hover:text-destructive transition-colors ml-1">
                    Retirer
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Inviter un membre */}
          <div className="pt-3 border-t border-border space-y-3">
            <p className="text-sm font-medium">Inviter un membre</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} type="email" placeholder="email@exemple.com" className="rounded-sm" />
              <Input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Nom (optionnel)" className="rounded-sm" />
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                className="border border-border rounded-sm px-3 py-2 text-sm bg-background">
                <option value="user">Utilisateur</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <Button onClick={inviteMember} disabled={inviting} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {inviting ? "Invitation…" : "Ajouter à l'équipe"}
            </Button>
            <p className="text-xs text-muted-foreground">Si l'email n'existe pas encore sur Acteio, un compte est créé automatiquement. Communiquez le mot de passe manuellement.</p>
          </div>
        </div>
      )}

      {/* MCP tokens */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
        <div className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Tokens API / MCP</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Permettent aux agents IA (Claude, Cursor…) d'accéder à Acteio via le protocole MCP,
          et d'authentifier les appels à l'API REST depuis vos propres outils (CRM, ERP, scripts).
          Chaque token est lié à votre compte. Disponible à partir du forfait Pro.
        </p>

        {/* Quota de clés alloué par l'ABONNEMENT (réglable par le super-admin dans
            Facturation → forfaits). Auparavant plafonné en dur à 3, sans rapport avec le
            forfait souscrit et sans que l'utilisateur sache où il en était. */}
        {tokenQuota && (
          <div data-testid="token-quota" className="flex items-center justify-between rounded-sm border border-border bg-background px-4 py-3">
            <span className="text-sm text-muted-foreground">
              Clés utilisées — forfait <span className="font-medium text-foreground">{tokenQuota.plan}</span>
            </span>
            <span className="font-mono text-sm font-semibold">
              {tokenQuota.max === -1
                ? `${tokenQuota.used} / illimité`
                : `${tokenQuota.used} / ${tokenQuota.max}`}
            </span>
          </div>
        )}

        {/* Documentation interactive de l'API (Swagger UI), servie par le backend sur /api/docs.
            Lien absolu construit depuis `API` (REACT_APP_BACKEND_URL + /api) : la doc n'est pas
            une route du frontend React, un <Link> react-router ne fonctionnerait pas. */}
        <a
          href={`${API}/docs`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="api-docs-link"
          className="flex items-center gap-3 rounded-sm border border-border bg-background p-4 transition-colors hover:border-foreground cursor-pointer"
        >
          <BookOpen className="w-5 h-5 text-primary shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium">Documentation de l'API</span>
            <span className="block text-xs text-muted-foreground">
              Référence interactive des endpoints, schémas et exemples — testez vos appels directement depuis la page.
            </span>
          </span>
          <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
        </a>

        {freshToken && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 space-y-2">
            <p className="text-sm font-semibold text-amber-800">Copiez ce token maintenant — il ne sera plus affiché !</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-white border border-amber-200 rounded-sm px-3 py-2 flex-1 break-all font-mono">{freshToken}</code>
              <Button size="sm" variant="outline" className="rounded-sm shrink-0"
                onClick={() => { navigator.clipboard.writeText(freshToken); toast.success("Copié !"); }}>
                Copier
              </Button>
            </div>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setFreshToken(null)}>Masquer</Button>
          </div>
        )}

        <div className="space-y-2">
          {mcpTokens.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun token créé.</p>
          ) : mcpTokens.map(t => (
            <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-xs text-muted-foreground font-mono">{t.prefix}… · Créé le {new Date(t.createdAt).toLocaleDateString("fr-FR")}{t.lastUsedAt ? ` · Utilisé le ${new Date(t.lastUsedAt).toLocaleDateString("fr-FR")}` : " · Jamais utilisé"}</p>
              </div>
              <Button size="sm" variant="outline" className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => revokeToken(t.id, t.name)}>
                Révoquer
              </Button>
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-border flex gap-3">
          <Input value={newTokenName} onChange={e => setNewTokenName(e.target.value)} placeholder="Nom du token (ex: Claude Desktop)" className="rounded-sm flex-1" />
          <Button onClick={createToken} disabled={creatingToken} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold shrink-0">
            {creatingToken ? "Création…" : "Créer un token"}
          </Button>
        </div>
      </div>

      {/* Webhooks sortants */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
        <div className="flex items-center gap-2">
          <Webhook className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Webhooks</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Recevez une notification HTTP signée (HMAC) à chaque événement Acteio (consultation,
          décision, question, acompte réglé) au lieu de sonder l'API. Disponible à partir du
          forfait Pro. Voir la <a href={`${API}/docs`} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">documentation de l'API</a> pour le format de signature.
        </p>

        {freshWebhookSecret && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 space-y-2">
            <p className="text-sm font-semibold text-amber-800">Copiez ce secret maintenant — il ne sera plus affiché !</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-white border border-amber-200 rounded-sm px-3 py-2 flex-1 break-all font-mono">{freshWebhookSecret}</code>
              <Button size="sm" variant="outline" className="rounded-sm shrink-0"
                onClick={() => { navigator.clipboard.writeText(freshWebhookSecret); toast.success("Copié !"); }}>
                Copier
              </Button>
            </div>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setFreshWebhookSecret(null)}>Masquer</Button>
          </div>
        )}

        <div className="space-y-3">
          {webhooks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun webhook configuré.</p>
          ) : webhooks.map((wh) => (
            <div key={wh.id} className="border border-border rounded-sm">
              <div className="flex items-center gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{wh.name || wh.url}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{wh.url}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {wh.events.map((ev) => (
                      <Badge key={ev} variant="outline" className="text-[10px] font-normal">
                        {WEBHOOK_EVENTS.find((e) => e.value === ev)?.label || ev}
                      </Badge>
                    ))}
                  </div>
                  {!wh.enabled && (
                    <p className="text-xs text-destructive mt-1.5">
                      Désactivé{wh.disabledAt ? ` le ${new Date(wh.disabledAt).toLocaleDateString("fr-FR")}` : ""} après {wh.consecutiveFailures} échecs consécutifs de livraison.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={wh.enabled} onCheckedChange={() => toggleWebhookEnabled(wh)} />
                  <Button size="sm" variant="outline" className="rounded-sm" disabled={testingWebhookId === wh.id}
                    onClick={() => testWebhook(wh.id)}>
                    {testingWebhookId === wh.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Tester"}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => loadDeliveries(wh.id)}>
                    {expandedWebhookId === wh.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={() => deleteWebhook(wh.id, wh.url)}>
                    Révoquer
                  </Button>
                </div>
              </div>

              {expandedWebhookId === wh.id && (
                <div className="border-t border-border p-3 bg-muted/30 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Dernières livraisons</p>
                  {loadingDeliveries ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : (webhookDeliveries[wh.id] || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">Aucune livraison pour l'instant.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(webhookDeliveries[wh.id] || []).map((d) => (
                        <div key={d.id} className="flex items-center gap-2 text-xs py-1 border-b border-border/60 last:border-0">
                          <Badge variant={WEBHOOK_STATUS_BADGE[d.status]?.variant || "outline"} className="text-[10px] shrink-0">
                            {WEBHOOK_STATUS_BADGE[d.status]?.label || d.status}
                          </Badge>
                          <span className="font-mono shrink-0">{d.eventType}</span>
                          <span className="text-muted-foreground shrink-0">{d.attempts} tentative(s)</span>
                          {d.responseStatus != null && <span className="text-muted-foreground shrink-0">HTTP {d.responseStatus}</span>}
                          <span className="text-muted-foreground truncate flex-1">{d.createdAt ? new Date(d.createdAt).toLocaleString("fr-FR") : ""}</span>
                          {d.status === "failed" && (
                            <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 shrink-0" disabled={replayingDeliveryId === d.id}
                              onClick={() => replayDelivery(d.id, wh.id)}>
                              {replayingDeliveryId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-border space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input value={newWebhook.url} onChange={(e) => setNewWebhook((w) => ({ ...w, url: e.target.value }))}
              placeholder="https://votre-crm.example.com/hooks/acteio" className="rounded-sm flex-1" />
            <Input value={newWebhook.name} onChange={(e) => setNewWebhook((w) => ({ ...w, name: e.target.value }))}
              placeholder="Nom (optionnel)" className="rounded-sm sm:max-w-[220px]" />
          </div>
          <div className="flex flex-wrap gap-3">
            {WEBHOOK_EVENTS.map((ev) => (
              <label key={ev.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <Checkbox checked={newWebhook.events.includes(ev.value)} onCheckedChange={() => toggleNewWebhookEvent(ev.value)} />
                {ev.label}
              </label>
            ))}
          </div>
          <Button onClick={createWebhook} disabled={creatingWebhook} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {creatingWebhook ? "Création…" : "Créer un webhook"}
          </Button>
        </div>
      </div>

      {/* Sécurité — changement de mot de passe */}
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Sécurité</h3>
        </div>
        <div className="space-y-3 max-w-sm">
          <div>
            <Label>Mot de passe actuel</Label>
            <Input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Nouveau mot de passe</Label>
            <Input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Confirmer le nouveau mot de passe</Label>
            <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} className="mt-1.5 rounded-sm" />
          </div>
          <Button onClick={changePassword} disabled={savingPw} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold w-full">
            {savingPw ? "Mise à jour…" : "Changer le mot de passe"}
          </Button>
        </div>
      </div>

      <BrandExtractorDialog open={extractor} setOpen={setExtractor} onDone={fetchCompanies} />
    </div>
  );
}
